/**
 * Small device widgets shared outside Team → Devices (the Team page tabs, the upload page's local files). Kept
 * apart from DevicesPage so that page can load lazily and stay out of the entry chunk (web bundle budget).
 */
import { NavLink } from 'react-router';
import { useT } from '@/i18n';
import { can } from '@/lib/constants';
import { useMembership } from '@/store/session';
import type { DeviceStatus } from '@/lib/api/types';

const STATUS_STYLE: Record<DeviceStatus, [string, string]> = {
  online: ['var(--green-soft)', 'var(--green)'],
  offline: ['var(--raise)', 'var(--muted)'],
  revoked: ['var(--red-soft)', 'var(--accent-text)'],
};

/** Members | Devices, shown on both Team pages. */
export function TeamTabs() {
  const { t } = useT();
  const membership = useMembership();
  if (!can(membership?.role, 'videos.edit')) return null;
  return (
    <nav className="segmented" aria-label={t('nTeam')} style={{ alignSelf: 'flex-start', justifySelf: 'start', width: 'fit-content' }}>
      {/* NavLink sets aria-current="page" and the "active" class on the current tab. */}
      <NavLink to="/team" end>{t('teamTabMembers')}</NavLink>
      <NavLink to="/team/devices">{t('nDevices')}</NavLink>
    </nav>
  );
}

export function DeviceStatusPill({ status }: { status: DeviceStatus }) {
  const { t } = useT();
  const [bg, fg] = STATUS_STYLE[status];
  const label = status === 'online' ? t('devOnline') : status === 'offline' ? t('devOffline') : t('devRevoked');
  return (
    <span className="pill" style={{ background: bg, color: fg }}>
      <span aria-hidden style={{ width: 6, height: 6, borderRadius: 3, background: fg, display: 'inline-block' }} />
      {label}
    </span>
  );
}
